import { yupResolver } from '@hookform/resolvers/yup';
import {
  BadgeOutlined,
  ChildCareOutlined,
  Close,
  Numbers,
  PercentOutlined,
  PersonOutline,
  SentimentSatisfiedAltOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { getColor } from '../../../../../assets/theme';
import InputFieldsController from '../../../../../components/form/controller/input-fields.controller';
import { applyDiscountSchema } from '../../../interface/room-posting-schema-interface';

import {
  usePostRegularDiscountMutation,
  usePostSpecialDiscountMutation,
} from '../../../../../api/front-office/roomposting.api.rtk';
import LoadingComponent from '../../../../../components/animation/loading-component';
import useAppContext from '../../../../../hooks/useAppContext';
import useInformationDialog from '../../../../../hooks/useInformationDialog';
import useToast from '../../../../../hooks/useToast';
import {
  IDiscountBreakdown,
  IDiscountSetting,
  IPaxBreakdown,
} from '../../../interface/charging-station';
import {
  RoomPostingRegularDiscounts,
  RoomPostingSpecialDiscounts,
} from '../../../interface/room-posting-interface';

import DatePickerController, {
  reversDatePickerStyle,
} from '@components/form/controller/date-picker.controller';
import ContainerTitle from '@components/ui/container-title';
import { LoadingButton } from '@mui/lab';
import { TinPatternFormat } from '@utils/format-component';
import moment, { Moment } from 'moment';
import CustomNumberFormat, {
  CustomNumberFormatProps,
} from '../../../../../components/form/custom-number-format';
import { parseGenparNumber } from '@utils/format';
import { DiscountResult } from '@modules/point-of-sale/interface/bo-interface/discount-list.interface';

export interface FormValues {
  manual_discount?: any; // will refactor to add valid interface
  percentage_discount?: any; // will refactor to add valid interface
  pwd_pax?: any; // will refactor to add valid interface
  athlete_pax?: any; // will refactor to add valid interface
  senior_pax?: any; // will refactor to add valid interface
  solo_parent_pax?: any; // will refactor to add valid interface
  senior_id?: SeniorPWDAthleteID | null;
  pwd_id?: SeniorPWDAthleteID | null;
  athlete_id?: SeniorPWDAthleteID | null;
  solo_parent_id?: SoloParentID | null;
}

interface SoloParentID {
  id_list?:
    | {
        id: string;
        name: string;
        childName: string;
        birthdate: Moment;
        age: number;
        tin?: string;
      }[]
    | null;
}

interface SeniorPWDAthleteID {
  id_list?: IDNo[] | null;
}

interface IDNo {
  id: string;
  name: string;
  childName?: string;
  birthdate?: Moment;
  age?: number;
  // SC - PWD
  tin?: string;
}

const FORoomPostingDiscountForm = ({
  handleClose,
  selectedIDs,
  fromModule,
  ledgerRefetch,
  useProperty,
  handleConfig,
  applyBtnTypeSubmit = true,
  onConfirm,
  totalPax = 1,
  disabledRegular,
  discountList,
  discounts,
  disabledPercentage,
  disabledManual,
  hideRemarks,
  disabledSpecial,
  maxAmount,
}: {
  handleClose: () => void;
  selectedIDs: string;
  fromModule?: 'Room Posting' | 'Charging Station' | 'Rates Plotting';
  ledgerRefetch: () => void;
  useProperty: string;
  handleConfig: (data: IDiscountSetting) => void;
  applyBtnTypeSubmit?: boolean; // for Rates Plotting use
  onConfirm?: (
    params: FormValues & {
      regularDiscount: boolean;
      lineRemarks: string;
      customDiscount: string;
      paxBreakdown: IPaxBreakdown;
    },
  ) => Promise<void>; // for Rates Plotting use
  totalPax?: number;
  maxAmount?: number;
  disabledRegular?: boolean;
  disabledSpecial?: boolean;
  disabledPercentage?: boolean;
  disabledManual?: boolean;
  discountList?: string[];
  discounts?: DiscountResult[];
  hideRemarks?: boolean;
}) => {
  const { primary, danger, white, success } = getColor();
  const { toast } = useToast();
  const {
    openSecCodeDialog,
    genpar: { getGenparValue },
  } = useAppContext();

  const DEFAULT_VAT_TYPE = getGenparValue('comp_vat_type')?.trim() || 'V';
  const DEFAULT_SC_RATE = parseGenparNumber(
    getGenparValue('comp_scrate_percentage'),
    0.1,
  );
  const DEFAULT_LT_RATE = parseGenparNumber(
    getGenparValue('comp_ltax_percentage'),
    0.02,
  );

  const [selectedDiscount, setSelectedDiscount] = useState('rbRegular');
  const [isFetching, setIsFetching] = useState(false);
  const regularDiscounts = discountList ?? ['10', '15', '20', '30', '50'];
  const [regularDiscountType, setRegularDiscountType] = useState('Percent');
  const [specialDiscountType, setSpecialDiscountType] = useState<
    'Senior' | 'PWD' | 'Athlete' | 'Solo Parent'
  >('Senior');
  const [selectChipPercent, setSelectChipPercent] = useState<number>(-1);
  const [selectedDiscountItem, setSelectedDiscountItem] =
    useState<DiscountResult | null>(null);
  const { DialogInformation, onShowInfoDialog } = useInformationDialog();

  const [inputRemarks, setInputRemarks] = useState<string>('');

  const [totalPaxPWD, setTotalPaxPWD] = useState(0);
  const [totalPaxSenior, setTotalPaxSenior] = useState(0);
  const [totalPaxAthlete, setTotalPaxAthlete] = useState(0);
  const [totalPaxSoloParent, setTotalPaxSoloParent] = useState(0);
  const pathname = window.location.pathname?.split('/')?.[1];
  //const [totalPaxAll, setTotalPaxAll] = useState(0);

  const methods = useForm<FormValues>({
    defaultValues: {
      manual_discount: 0,
      percentage_discount: 0,
      pwd_pax: 0,
      athlete_pax: 0,
      senior_pax: 0,
      solo_parent_pax: 0,
    },
    resolver: yupResolver<FormValues>(
      applyDiscountSchema(
        selectedDiscount === 'rbSpecial' ? 'Special' : 'Regular',
        regularDiscountType == 'Percent',
        specialDiscountType,
        totalPaxSenior,
        totalPaxPWD,
        totalPaxAthlete,
        totalPaxSoloParent,
        !!discountList,
      ),
    ),
    // mode: 'onChange',
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
    trigger,
    getValues,
    setFocus,
  } = methods;

  const {
    fields: field_athlete,
    append: append_athlete,
    remove: remove_athlete,
  } = useFieldArray({
    control: control,
    name: 'athlete_id.id_list',
  });
  const {
    fields: field_senior,
    append: append_senior,
    remove: remove_senior,
  } = useFieldArray({
    control: control,
    name: 'senior_id.id_list',
  });
  const {
    fields: field_pwd,
    append: append_pwd,
    remove: remove_pwd,
  } = useFieldArray({
    control: control,
    name: 'pwd_id.id_list',
  });
  const {
    fields: fieldSoloParent,
    append: appendFieldSoloParent,
    remove: removeFieldSoloParent,
  } = useFieldArray({
    control: control,
    name: 'solo_parent_id.id_list',
  });

  useEffect(() => {
    const fields: Array<keyof FormValues> = [
      'senior_id',
      'pwd_id',
      'solo_parent_id',
      'athlete_id',
    ];

    const idItemKeys = [
      'id',
      'name',
      'birthdate',
      'age',
      'childName',
      'tin',
    ] as const;

    for (const field of fields) {
      const items = (errors[field] as SeniorPWDAthleteID)?.id_list;
      if (!Array.isArray(items)) continue;

      for (let index = 0; index < items.length; index++) {
        const errItem = items[index];
        if (!errItem) continue;

        for (const key of idItemKeys) {
          if (errItem[key]) {
            setFocus(`${field}.id_list.${index}.${key}`);
            return;
          }
        }
      }
    }
  }, [errors, setFocus]);

  //Radio Button Regular Disc and SC Disc change
  const handleSelectedDiscount = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSelectedDiscount((event.target as HTMLInputElement).value);
  };

  //Chips Discounts handle change
  const handleChipsDiscountClick = (
    index: number,
    disc: number,
    discountItem?: DiscountResult | null,
  ) => {
    setSelectChipPercent(index);
    setSelectedDiscountItem(discountItem ?? null);

    // Amount-based discount
    if (discountItem && discountItem.isPercent === 0) {
      setRegularDiscountType('Amount');

      setValue('manual_discount', discountItem?.discountValue ?? disc ?? 0, {
        shouldValidate: true,
        shouldDirty: true,
      });

      setValue('percentage_discount', 0);
      return;
    }

    // Percentage-based discount
    setRegularDiscountType('Percent');

    setValue(
      'percentage_discount',
      discountItem ? (discountItem?.discountValue ?? disc ?? 0) * 100 : disc,
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );

    setValue('manual_discount', 0);
  };

  useEffect(() => {
    if (!disabledRegular || disabledSpecial) return;
    setSelectedDiscount('rbSpecial');
  }, [disabledRegular, disabledSpecial]);

  useEffect(() => {
    (async () => {
      try {
        setValue('percentage_discount', 0);
        setValue('pwd_pax', 0);
        setValue('athlete_pax', 0);
        setValue('senior_pax', 0);
        setValue('solo_parent_pax', 0);
      } catch (error) {
        console.log(error);
      }
    })();

    return () => {};
  }, []);

  useEffect(() => {
    if (discountList) return;

    const percentageDiscount = watch('percentage_discount');

    if (!percentageDiscount) return;

    if (parseInt(percentageDiscount, 10) > 0) {
      switch (parseInt(percentageDiscount, 10)) {
        case 10:
          setSelectChipPercent(0);
          break;
        case 15:
          setSelectChipPercent(1);
          break;
        case 20:
          setSelectChipPercent(2);
          break;
        case 30:
          setSelectChipPercent(3);
          break;
        case 50:
          setSelectChipPercent(4);
          break;

        default:
          setSelectChipPercent(-1);
      }

      setValue('percentage_discount', percentageDiscount);
      setValue('manual_discount', '0.00');
      setRegularDiscountType('Percent');
    }
  }, [watch('percentage_discount'), discountList]);

  useEffect(() => {
    const manualDiscount = watch('manual_discount');

    if (!manualDiscount) return;

    if (selectedDiscountItem && !selectedDiscountItem.isPercent) {
      return;
    }

    if (parseFloat(manualDiscount) > 0) {
      setSelectChipPercent(-1);
      setValue('percentage_discount', 0);
      setValue('manual_discount', manualDiscount);
      setRegularDiscountType('Amount');
    }
  }, [watch('manual_discount')]);

  useEffect(() => {
    if (watch('athlete_pax') < 0) return;

    (async () => {
      let curPax = 0;
      const seniorPax = watch('senior_pax');
      const athletePax = watch('athlete_pax');
      const pwdPax = watch('pwd_pax');
      const soloParentPax = watch('solo_parent_pax');

      curPax = athletePax;
      const totalApplyPax =
        parseInt(seniorPax, 10) +
        parseInt(athletePax, 10) +
        parseInt(pwdPax, 10) +
        parseInt(soloParentPax?.toString(), 10);

      if (athletePax > 0 && totalApplyPax > totalPax) {
        curPax = 0;
        setValue('athlete_pax', 0);
        onShowInfoDialog({
          desc: `Enter a valid number of pax.\nYou can only apply for a maximum ${totalPax} pax.`,
        });
        setTotalPaxAthlete(0);
      } else {
        setTotalPaxAthlete(parseInt(athletePax, 10));
      }
      const currentFieldsLength = field_athlete.length;
      const diff = curPax - currentFieldsLength;

      if (diff > 0) {
        for (let i = 0; i < diff; i++) {
          append_athlete({
            id: '',
            name: '',
          });
        }
      } else if (diff < 0) {
        for (let i = 0; i < -diff; i++) {
          remove_athlete(currentFieldsLength - 1 - i);
        }
      }

      setSpecialDiscountType('Athlete');

      if (parseInt(seniorPax, 10) < 0) {
        setSpecialDiscountType('Senior');
        return;
      }
      if (parseInt(pwdPax, 10) < 0) {
        setSpecialDiscountType('PWD');
        return;
      }
      if (parseInt(soloParentPax, 10) < 0) {
        setSpecialDiscountType('Solo Parent');
        return;
      }
    })();
  }, [watch('athlete_pax')]);

  useEffect(() => {
    if (watch('pwd_pax') < 0) return;

    (async () => {
      let curPax = 0;
      const seniorPax = watch('senior_pax');
      const athletePax = watch('athlete_pax');
      const pwdPax = watch('pwd_pax');
      const soloParentPax = watch('solo_parent_pax');

      curPax = pwdPax;
      const totalApplyPax =
        parseInt(seniorPax, 10) +
        parseInt(athletePax, 10) +
        parseInt(pwdPax, 10) +
        parseInt(soloParentPax?.toString(), 10);

      if (pwdPax > 0 && totalApplyPax > totalPax) {
        curPax = 0;
        setValue('pwd_pax', 0);
        onShowInfoDialog({
          desc: `Enter a valid number of pax.\nYou can only apply for a maximum ${totalPax} pax.`,
        });

        setTotalPaxPWD(0);
      } else {
        setTotalPaxPWD(parseInt(pwdPax, 10));
      }
      const currentFieldsLength = field_pwd.length;
      const diff = curPax - currentFieldsLength;

      if (diff > 0) {
        for (let i = 0; i < diff; i++) {
          append_pwd({
            id: '',
            name: '',
            tin: '',
          });
        }
      } else if (diff < 0) {
        for (let i = 0; i < -diff; i++) {
          remove_pwd(currentFieldsLength - 1 - i);
        }
      }

      setSpecialDiscountType('PWD');
      if (parseInt(seniorPax, 10) < 0) {
        setSpecialDiscountType('Senior');
        return;
      }
      if (parseInt(athletePax, 10) < 0) {
        setSpecialDiscountType('Athlete');
        return;
      }
      if (parseInt(soloParentPax, 10) < 0) {
        setSpecialDiscountType('Solo Parent');
        return;
      }
    })();
  }, [watch('pwd_pax')]);

  useEffect(() => {
    if (watch('solo_parent_pax') < 0) return;

    (async () => {
      let curPax = 0;
      const seniorPax = watch('senior_pax');
      const athletePax = watch('athlete_pax');
      const pwdPax = watch('pwd_pax');
      const soloParentPax = watch('solo_parent_pax');
      curPax = soloParentPax;
      const totalApplyPax =
        parseInt(seniorPax, 10) +
        parseInt(athletePax, 10) +
        parseInt(pwdPax, 10) +
        parseInt(soloParentPax?.toString(), 10);

      if (soloParentPax > 0 && totalApplyPax > totalPax) {
        curPax = 0;
        setValue('solo_parent_pax', 0);
        onShowInfoDialog({
          desc: `Enter a valid number of pax.\nYou can only apply for a maximum ${totalPax} pax.`,
        });
        setTotalPaxSoloParent(0);
      } else {
        setTotalPaxSoloParent(parseInt(soloParentPax?.toString(), 10));
      }
      const currentFieldsLength = fieldSoloParent.length;
      const diff = curPax - currentFieldsLength;

      if (diff > 0) {
        for (let i = 0; i < diff; i++) {
          appendFieldSoloParent({
            id: '',
            name: '',
            childName: '',
            birthdate: moment(),
            age: 0,
          });
        }
      } else if (diff < 0) {
        for (let i = 0; i < -diff; i++) {
          removeFieldSoloParent(currentFieldsLength - 1 - i);
        }
      }

      setSpecialDiscountType('Solo Parent');
      if (parseInt(athletePax, 10) < 0) {
        setSpecialDiscountType('Athlete');
        return;
      }
      if (parseInt(pwdPax, 10) < 0) {
        setSpecialDiscountType('PWD');
        return;
      }
      if (parseInt(seniorPax, 10) < 0) {
        setSpecialDiscountType('Senior');
        return;
      }
    })();
  }, [watch('solo_parent_pax')]);

  useEffect(() => {
    if (watch('senior_pax') < 0) return;

    (async () => {
      let curPax = 0;
      const seniorPax = watch('senior_pax');
      const athletePax = watch('athlete_pax');
      const pwdPax = watch('pwd_pax');
      const soloParentPax = watch('solo_parent_pax');

      curPax = seniorPax;
      const totalApplyPax =
        parseInt(seniorPax, 10) +
        parseInt(athletePax, 10) +
        parseInt(pwdPax, 10) +
        parseInt(soloParentPax?.toString(), 10);

      if (seniorPax > 0 && totalApplyPax > totalPax) {
        curPax = 0;
        setValue('senior_pax', 0);
        onShowInfoDialog({
          desc: `Enter a valid number of pax.\nYou can only apply for a maximum ${totalPax} pax.`,
        });
        setTotalPaxSenior(0);
      } else {
        setTotalPaxSenior(parseInt(seniorPax, 10));
      }
      const currentFieldsLength = field_senior.length;
      const diff = curPax - currentFieldsLength;

      if (diff > 0) {
        for (let i = 0; i < diff; i++) {
          append_senior({
            id: '',
            name: '',
            tin: '',
          });
        }
      } else if (diff < 0) {
        for (let i = 0; i < -diff; i++) {
          remove_senior(currentFieldsLength - 1 - i);
        }
      }

      setSpecialDiscountType('Senior');
      if (parseInt(athletePax, 10) < 0) {
        setSpecialDiscountType('Athlete');
        return;
      }
      if (parseInt(pwdPax, 10) < 0) {
        setSpecialDiscountType('PWD');
        return;
      }
      if (parseInt(soloParentPax, 10) < 0) {
        setSpecialDiscountType('Solo Parent');
        return;
      }
    })();
  }, [watch('senior_pax')]);

  const handleRemarks = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputRemarks(event.target.value);
  };

  const toPaxCount = (value: unknown) => {
    const paxCount = Number(value);

    return Number.isFinite(paxCount) && paxCount > 0 ? paxCount : 0;
  };

  const getPaxBreakdown = (
    data: FormValues,
    isSpecialDiscount: boolean,
  ): IPaxBreakdown => {
    const seniorPax = isSpecialDiscount ? toPaxCount(data.senior_pax) : 0;
    const pwdPax = isSpecialDiscount ? toPaxCount(data.pwd_pax) : 0;
    const athletePax = isSpecialDiscount ? toPaxCount(data.athlete_pax) : 0;
    const soloParentPax = isSpecialDiscount
      ? toPaxCount(data.solo_parent_pax)
      : 0;

    return {
      Regular: isSpecialDiscount ? 0 : 1,
      Senior: seniorPax,
      PWD: pwdPax,
      Ath: athletePax,
      SoloParent: soloParentPax,
    };
  };

  const formatSpecialDiscounts = (data: FormValues) => {
    // Senior
    const seniorList = data?.senior_id?.id_list?.map((id) => ({
      discount_type: 'Senior Citizen',
      id_no: id.id,
    }));
    // PWD
    const PWDList = data?.pwd_id?.id_list?.map((id) => ({
      discount_type: 'PWD',
      id_no: id.id,
    }));
    // Athlete
    const athleteList = data?.athlete_id?.id_list?.map((id) => ({
      discount_type: 'Athlete',
      id_no: id.id,
    }));
    // SoloParent
    const soloParentList = data?.solo_parent_id?.id_list?.map((id) => ({
      discount_type: 'Solo Parent',
      id_no: id.id,
    }));
    return {
      'Special Discount': [
        ...(seniorList ?? []),
        ...(PWDList ?? []),
        ...(athleteList ?? []),
        ...(soloParentList ?? []),
      ],
    };
  };

  //API Transfer Folio
  const [postRegularDiscount] = usePostRegularDiscountMutation();
  const [postSpecialDiscount] = usePostSpecialDiscountMutation();

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    if (selectedIDs.length === 0) {
      onShowInfoDialog({
        desc: 'Please select item(s) to apply discount.',
      });

      return;
    }

    //Module Room Posting
    if (fromModule === 'Room Posting') {
      openSecCodeDialog(async (result) => {
        if (!result?.data.isValid) {
          return;
        }
        setIsFetching(true);
        //Regular Discount
        if (selectedDiscount === 'rbRegular') {
          const { manual_discount, percentage_discount } = data;

          const postApplyRegularDiscount: RoomPostingRegularDiscounts = {
            properties: [
              {
                property: {
                  propertyID: useProperty,
                  trans_list: [
                    {
                      identifier_list: selectedIDs,
                      discount:
                        regularDiscountType == 'Percent'
                          ? percentage_discount / 100
                          : manual_discount,
                      is_percent: regularDiscountType,
                      discount_remarks: inputRemarks,
                    },
                  ],
                },
              },
            ],
          };

          await postRegularDiscount(postApplyRegularDiscount)
            .unwrap()
            .then(() => {
              toast({
                severity: 'success',
                message: `Successfully applied regular discounts.`,
              });
              handleClose();
              ledgerRefetch();
            })
            .catch((error) =>
              toast({
                severity: 'error',
                message: `Error occurred apply regular discounts: ${error}`,
              }),
            )
            .finally(() => {
              setIsFetching(false);
            });
        }

        //Special Discount
        if (selectedDiscount === 'rbSpecial') {
          //Format JSON
          const specialDiscountsData = formatSpecialDiscounts(data);
          const postApplySpecialDiscount: RoomPostingSpecialDiscounts = {
            properties: [
              {
                property: {
                  propertyID: useProperty,
                  trans_list: [
                    {
                      identifier_list: selectedIDs,
                      sd_list: specialDiscountsData,
                    },
                  ],
                },
              },
            ],
          };

          await postSpecialDiscount(postApplySpecialDiscount)
            .unwrap()
            .then(() => {
              toast({
                severity: 'success',
                message: `Successfully applied special discounts.`,
              });
              handleClose();
              ledgerRefetch();
            })
            .catch((error) =>
              toast({
                severity: 'error',
                message: `Error occurred apply special discounts: ${error}`,
              }),
            )
            .finally(() => {
              setIsFetching(false);
            });
        }
      }, 'issue_disc');
    }

    //Module Charging Station
    if (fromModule === 'Charging Station') {
      openSecCodeDialog(async (result) => {
        if (!result?.data.isValid) {
          return;
        }
        setIsFetching(true);
        const isSpecialDiscount: boolean = selectedDiscount === 'rbSpecial';
        const selectedIdArr: number[] = selectedIDs.split(',').map(Number);
        const specialDiscList = formatSpecialDiscounts(data);
        const paxBreakdown = getPaxBreakdown(data, isSpecialDiscount);
        const { manual_discount, percentage_discount } = data;
        const discountBreakdown: IDiscountBreakdown = {
          amount: isSpecialDiscount ? 0 : manual_discount,
          percent: isSpecialDiscount ? 0 : percentage_discount / 100,
        };

        const discountSetting: IDiscountSetting = {
          selectedId: selectedIdArr,
          pax: paxBreakdown,
          discount: discountBreakdown,
          transSCRate: DEFAULT_SC_RATE,
          transLTRate: DEFAULT_LT_RATE,
          transVatType: DEFAULT_VAT_TYPE,
          transDiscountRemarks: isSpecialDiscount ? '' : inputRemarks,
          rawDiscount: specialDiscList,
        };

        handleConfig(discountSetting);
        handleClose();
      }, 'issue_disc');
    }
  };

  // HANDLE MANUAL SUBMIT
  const [isLoadingManualSubmit, setIsLoadingManualSubmit] = useState(false);

  const handleManualSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) return;

    if (
      fromModule == 'Rates Plotting' &&
      !inputRemarks &&
      selectedDiscount !== 'rbSpecial' &&
      !hideRemarks
    ) {
      toast({ message: 'Remarks is required field', severity: 'warning' });
      return;
    }

    const data = getValues();
    const regularDiscount = selectedDiscount === 'rbRegular';
    const paxBreakdown = getPaxBreakdown(data, !regularDiscount);

    setIsLoadingManualSubmit(true);
    onConfirm &&
      (await onConfirm({
        ...data,
        regularDiscount,
        lineRemarks: inputRemarks,
        customDiscount:
          selectedDiscountItem?.discountAcronym ??
          discountList?.[selectChipPercent] ??
          '',
        paxBreakdown,
      }));

    setIsLoadingManualSubmit(false);
  };

  return (
    <FormProvider {...methods}>
      <Box sx={{ minWidth: '370px' }}>
        {/* HEADER */}
        <Stack
          direction='row'
          justifyContent='space-between'
          borderBottom='1px solid #EEE'
          p={2}
        >
          <ContainerTitle
            title='Apply Discount'
            titleStyle={{ fontWeight: 500, fontSize: '18px!important' }}
            func={() => {}}
          />
          <IconButton color='inherit' onClick={handleClose}>
            <Close />
          </IconButton>
        </Stack>

        {isFetching && <LoadingComponent />}

        {/* MAIN */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack
            sx={{
              p: 2,
              maxHeight: '600px',
              overflow: 'auto',
            }}
            direction='row'
            gap='8px'
            width='100%'
          >
            <FormControl sx={{ width: '100%' }} className='form-control'>
              <RadioGroup
                row
                value={selectedDiscount}
                onChange={handleSelectedDiscount}
                defaultValue='rbRegular'
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  gap={2}
                  width='100%'
                >
                  {/* Column Regular Discount */}
                  <Stack gap={2} width={{ xs: '100%', sm: '50%' }}>
                    <FormControlLabel
                      value='rbRegular'
                      control={<Radio />}
                      label='Regular Discounts'
                      sx={{
                        borderBottom: `1px ${selectedDiscount === 'rbRegular' ? 'solid ' + primary : 'dashed #EEE'}`,
                        whiteSpace: 'nowrap',
                        margin: 0,
                      }}
                      disabled={disabledRegular}
                    />
                    {/* CHIPS */}
                    <Stack
                      spacing={1}
                      direction='row'
                      sx={{ overflow: 'auto' }}
                    >
                      {regularDiscounts.map((disc, index) => (
                        <Chip
                          key={index}
                          label={discountList ? disc : `${disc}%`}
                          clickable
                          onClick={() =>
                            handleChipsDiscountClick(
                              index,
                              parseFloat(disc),
                              discounts?.find(
                                (item) => item.discountAcronym === disc,
                              ) ?? null,
                            )
                          }
                          sx={{
                            backgroundColor: white,
                            color:
                              index !== selectChipPercent ? '#000000' : success,
                            borderColor:
                              index !== selectChipPercent ? '#bdbdbd' : success,
                            width: discountList ? 'fit-content' : '65px',
                            height: '32px',
                            borderRadius: '100px',
                            padding: '4px',
                          }}
                          variant='outlined'
                          disabled={selectedDiscount !== 'rbRegular'}
                        />
                      ))}
                    </Stack>

                    {/* FIELDS */}
                    <Stack direction={{ xs: 'row' }} gap={2}>
                      <InputFieldsController
                        label='Manual Discount'
                        name='manual_discount'
                        control={control}
                        errors={errors}
                        inputProps={{
                          readOnly: disabledManual,
                        }}
                        InputProps={{
                          inputComponent: CustomNumberFormat,
                          inputProps: {
                            maxAmount,
                            maxLength: 16,
                            textAlign: 'right',
                          },
                        }}
                        disabled={
                          selectedDiscount !== 'rbRegular' ||
                          (disabledManual && !selectedDiscountItem?.isVarying)
                        }
                        wrapperStyle={{
                          marginBottom: 0,
                        }}
                      />

                      <InputFieldsController
                        label='Discount in %'
                        name='percentage_discount'
                        control={control}
                        errors={errors}
                        type='number'
                        inputProps={{
                          style: { textAlign: 'right' },
                          readOnly: disabledPercentage,
                        }}
                        InputProps={{
                          startAdornment: <PercentOutlined />,
                        }}
                        disabled={
                          selectedDiscount !== 'rbRegular' || disabledPercentage
                        }
                        wrapperStyle={{
                          marginBottom: 0,
                        }}
                      />
                    </Stack>

                    {!hideRemarks && (
                      <InputFieldsController
                        label={
                          fromModule == 'Rates Plotting'
                            ? 'Remarks *'
                            : 'Remarks'
                        }
                        name='line_remarks'
                        placeholder='Input Remarks...'
                        onChange={handleRemarks}
                        value={inputRemarks}
                        helperText={`${inputRemarks?.length}/100`}
                        control={control}
                        errors={errors}
                        inputProps={{ maxLength: 100 }}
                        multiline
                        rows={3}
                        wrapperStyle={{
                          marginBottom: 0,
                        }}
                        borderErrorOnly
                        disabled={selectedDiscount !== 'rbRegular'}
                      />
                    )}
                  </Stack>

                  {/* Column Special Discount */}
                  <Stack gap={1} width={{ xs: '100%', sm: '50%' }}>
                    <FormControlLabel
                      value='rbSpecial'
                      control={<Radio />}
                      label='Special Discounts'
                      sx={{
                        borderBottom: `1px ${selectedDiscount === 'rbSpecial' ? 'solid ' + primary : 'dashed #EEE'}`,
                        whiteSpace: 'nowrap',
                        margin: 0,
                      }}
                      disabled={disabledSpecial}
                    />
                    <Stack
                      gap={2}
                      sx={{ maxHeight: '300px', overflow: 'auto' }}
                    >
                      {/*Senior Citizen */}
                      <Stack direction='column' gap='5px'>
                        {/* <Typography>Senior Citizen</Typography> */}
                        <InputFieldsController
                          label='Senior Citizen Total Pax'
                          name='senior_pax'
                          control={control}
                          errors={errors}
                          wrapperStyle={{
                            minWidth: '150px',
                            width: '100%',
                            marginBottom: 0,
                          }}
                          inputProps={{
                            min: 0,
                            maxLength: totalPax,
                            style: { textAlign: 'right' },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position='start'>
                                <Numbers />
                              </InputAdornment>
                            ),
                          }}
                          type='number'
                          disabled={selectedDiscount !== 'rbSpecial'}
                          value={
                            selectedDiscount !== 'rbSpecial' ? 0 : undefined
                          }
                          onChange={(e) => {
                            const el = e.target;
                            let value = el.value
                              .trim()
                              .replace(/[^\d]|^0+/g, '');
                            const numericValue = Number(value);

                            if (!numericValue || numericValue <= 0) {
                              setValue('senior_pax', 0);
                              el.value = '0';
                              return;
                            }

                            setValue('senior_pax', numericValue);
                            el.value = String(numericValue);
                          }}
                          sx={{ mt: 1 }}
                        />

                        {totalPaxSenior > 0 && (
                          <Typography>Senior Citizen ID List</Typography>
                        )}

                        <Stack
                          direction='column'
                          sx={{
                            maxHeight: '185px',
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': {
                              width: '10px', // Width of the scrollbar
                            },
                          }}
                        >
                          {totalPaxSenior > 0 && totalPaxSenior <= 100 && (
                            <>
                              {errors.senior_id?.id_list?.message && (
                                <Typography
                                  sx={{
                                    fontFamily: 'Poppins',
                                    fontSize: 12,
                                    marginBottom: 1,
                                    marginTop: -1,
                                    marginLeft: 1,
                                    fontWeight: 400,
                                    lineHeight: '19.92px',
                                    letterSpacing: '0.4px',
                                    color: danger,
                                  }}
                                >
                                  {errors.senior_id?.id_list?.message}
                                </Typography>
                              )}
                              <Stack gap={2}>
                                {[...Array(totalPaxSenior)].map((_, index) => (
                                  <Stack
                                    key={index}
                                    sx={{
                                      py: 1.5,
                                      px: 1,
                                      border: '2px solid',
                                      borderColor: 'secondary.500',
                                      borderRadius: '12px',
                                    }}
                                  >
                                    <InputFieldsController
                                      label={`Senior ID No. ${index + 1} <span style="fontStyle: italic; color: #FF5630;" }}>${
                                        errors.senior_id?.id_list?.[index]?.id
                                          ?.message ?? ''
                                      }</span>`}
                                      name={`senior_id.id_list[${index}].id`}
                                      control={control}
                                      errors={errors}
                                      disabled={
                                        selectedDiscount !== 'rbSpecial'
                                      }
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position='start'>
                                            <BadgeOutlined />
                                          </InputAdornment>
                                        ),
                                        inputComponent: (
                                          props: CustomNumberFormatProps,
                                        ) => (
                                          <CustomNumberFormat
                                            {...props}
                                            isNegative={false}
                                            decimalScale={0}
                                            isAllowed={(values) =>
                                              values.value.length <= 16
                                            }
                                            thousandSeparator={false}
                                          />
                                        ),
                                      }}
                                      trigger={trigger}
                                      validateTrigger={Boolean(
                                        errors.senior_id?.id_list?.[index]?.id
                                          ?.message ?? false,
                                      )}
                                      value={
                                        selectedDiscount !== 'rbSpecial'
                                          ? 0
                                          : undefined
                                      }
                                    />

                                    <InputFieldsController
                                      label={`Name ${index + 1} <span style="fontStyle: italic; color: #FF5630;" }}>${
                                        errors.senior_id?.id_list?.[index]?.name
                                          ?.message ?? ''
                                      }</span>`}
                                      name={`senior_id.id_list[${index}].name`}
                                      control={control}
                                      errors={errors}
                                      disabled={
                                        selectedDiscount !== 'rbSpecial'
                                      }
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position='start'>
                                            <PersonOutline />
                                          </InputAdornment>
                                        ),
                                      }}
                                      trigger={trigger}
                                      validateTrigger={Boolean(
                                        errors.senior_id?.id_list?.[index]?.name
                                          ?.message ?? false,
                                      )}
                                    />

                                    <InputFieldsController
                                      label={`TIN ${index + 1} <span style="fontStyle: italic; color: #FF5630;" }}>${
                                        errors.senior_id?.id_list?.[index]?.tin
                                          ?.message ?? ''
                                      }</span>`}
                                      name={`senior_id.id_list[${index}].tin`}
                                      control={control}
                                      errors={errors}
                                      disabled={
                                        selectedDiscount !== 'rbSpecial'
                                      }
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position='start'>
                                            <PersonOutline />
                                          </InputAdornment>
                                        ),
                                        inputComponent: TinPatternFormat,
                                      }}
                                      trigger={trigger}
                                      validateTrigger={Boolean(
                                        errors.senior_id?.id_list?.[index]?.tin
                                          ?.message ?? false,
                                      )}
                                      wrapperStyle={{ mb: 0 }}
                                    />
                                  </Stack>
                                ))}
                              </Stack>
                            </>
                          )}
                        </Stack>

                        {totalPaxSenior > 0 && (
                          <Divider
                            sx={{
                              pt: 1,
                              borderColor: 'primary.main',
                              borderBottomWidth: '2px',
                            }}
                          />
                        )}
                      </Stack>

                      {/**PWD */}
                      <Stack direction='column' gap='5px'>
                        {/* <Typography>Person with Disability</Typography> */}

                        <InputFieldsController
                          label='Person with Disability Total Pax'
                          name='pwd_pax'
                          control={control}
                          errors={errors}
                          wrapperStyle={{
                            minWidth: '150px',
                            width: '100%',
                            marginBottom: 0,
                          }}
                          inputProps={{
                            min: 0,
                            maxLength: totalPax,
                            style: { textAlign: 'right' },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position='start'>
                                <Numbers />
                              </InputAdornment>
                            ),
                          }}
                          type='number'
                          disabled={selectedDiscount !== 'rbSpecial'}
                          value={
                            selectedDiscount !== 'rbSpecial' ? 0 : undefined
                          }
                          onChange={(e) => {
                            const el = e.target;
                            let value = el.value
                              .trim()
                              .replace(/[^\d]|^0+/g, '');
                            const numericValue = Number(value);

                            if (!numericValue || numericValue <= 0) {
                              setValue('pwd_pax', 0);
                              el.value = '0';
                              return;
                            }

                            setValue('pwd_pax', numericValue);
                            el.value = String(numericValue);
                          }}
                        />
                        {totalPaxPWD > 0 && (
                          <Typography>PWD ID List</Typography>
                        )}

                        <Stack
                          direction='column'
                          sx={{
                            maxHeight: '185px',
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': {
                              width: '10px', // Width of the scrollbar
                            },
                          }}
                        >
                          {totalPaxPWD > 0 && totalPaxPWD <= 100 && (
                            <>
                              {errors.pwd_id?.id_list?.message && (
                                <Typography
                                  sx={{
                                    fontFamily: 'Poppins',
                                    fontSize: 12,
                                    marginBottom: 1,
                                    marginTop: -1,
                                    marginLeft: 1,
                                    fontWeight: 400,
                                    lineHeight: '19.92px',
                                    letterSpacing: '0.4px',
                                    color: danger,
                                  }}
                                >
                                  {errors.pwd_id?.id_list?.message}
                                </Typography>
                              )}
                              <Stack gap={2}>
                                {[...Array(totalPaxPWD)].map((_, index) => (
                                  <Stack
                                    key={index}
                                    sx={{
                                      py: 1.5,
                                      px: 1,
                                      border: '2px solid',
                                      borderColor: 'secondary.500',
                                      borderRadius: '12px',
                                    }}
                                  >
                                    <InputFieldsController
                                      label={`PWD ID No. ${index + 1} <span style="fontStyle: italic; color: #FF5630;" }}>${
                                        errors.pwd_id?.id_list?.[index]?.id
                                          ?.message ?? ''
                                      }</span>`}
                                      name={`pwd_id.id_list[${index}].id`}
                                      control={control}
                                      errors={errors}
                                      disabled={
                                        selectedDiscount !== 'rbSpecial'
                                      }
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position='start'>
                                            <BadgeOutlined />
                                          </InputAdornment>
                                        ),
                                        inputComponent: (
                                          props: CustomNumberFormatProps,
                                        ) => (
                                          <CustomNumberFormat
                                            {...props}
                                            isNegative={false}
                                            decimalScale={0}
                                            isAllowed={(values) =>
                                              values.value.length <= 16
                                            }
                                            thousandSeparator={false}
                                          />
                                        ),
                                      }}
                                      trigger={trigger}
                                      validateTrigger={Boolean(
                                        errors.pwd_id?.id_list?.[index]?.id
                                          ?.message ?? false,
                                      )}
                                      value={
                                        selectedDiscount !== 'rbSpecial'
                                          ? 0
                                          : undefined
                                      }
                                    />

                                    <InputFieldsController
                                      label={`Name ${index + 1} <span style="fontStyle: italic; color: #FF5630;" }}>${
                                        errors.pwd_id?.id_list?.[index]?.name
                                          ?.message ?? ''
                                      }</span>`}
                                      name={`pwd_id.id_list[${index}].name`}
                                      control={control}
                                      errors={errors}
                                      disabled={
                                        selectedDiscount !== 'rbSpecial'
                                      }
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position='start'>
                                            <PersonOutline />
                                          </InputAdornment>
                                        ),
                                      }}
                                      trigger={trigger}
                                      validateTrigger={Boolean(
                                        errors.pwd_id?.id_list?.[index]?.name
                                          ?.message ?? false,
                                      )}
                                    />

                                    <InputFieldsController
                                      label={`TIN ${index + 1} <span style="fontStyle: italic; color: #FF5630;" }}>${
                                        errors.pwd_id?.id_list?.[index]?.tin
                                          ?.message ?? ''
                                      }</span>`}
                                      name={`pwd_id.id_list[${index}].tin`}
                                      control={control}
                                      errors={errors}
                                      disabled={
                                        selectedDiscount !== 'rbSpecial'
                                      }
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position='start'>
                                            <PersonOutline />
                                          </InputAdornment>
                                        ),
                                        inputComponent: TinPatternFormat,
                                      }}
                                      trigger={trigger}
                                      validateTrigger={Boolean(
                                        errors.pwd_id?.id_list?.[index]?.tin
                                          ?.message ?? false,
                                      )}
                                      wrapperStyle={{ mb: 0 }}
                                    />
                                  </Stack>
                                ))}
                              </Stack>
                            </>
                          )}
                        </Stack>

                        {totalPaxPWD > 0 && (
                          <Divider
                            sx={{
                              pt: 1,
                              borderColor: 'primary.main',
                              borderBottomWidth: '2px',
                            }}
                          />
                        )}
                      </Stack>

                      {/*Solo Parent*/}
                      <Stack direction='column' gap='5px'>
                        <InputFieldsController
                          label='Solo Parent Total Pax'
                          name='solo_parent_pax'
                          control={control}
                          errors={errors}
                          wrapperStyle={{
                            minWidth: '150px',
                            width: '100%',
                            marginBottom: 0,
                          }}
                          inputProps={{
                            min: 0,
                            maxLength: totalPax,
                            style: { textAlign: 'right' },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position='start'>
                                <Numbers />
                              </InputAdornment>
                            ),
                          }}
                          type='number'
                          disabled={selectedDiscount !== 'rbSpecial'}
                          onChange={(e) => {
                            const el = e.target;
                            let value = el.value
                              .trim()
                              .replace(/[^\d]|^0+/g, '');
                            const numericValue = Number(value);

                            if (!numericValue || numericValue <= 0) {
                              setValue('solo_parent_pax', 0);
                              el.value = '0';
                              return;
                            }

                            setValue('solo_parent_pax', numericValue);
                            el.value = String(numericValue);
                          }}
                        />
                        {totalPaxSoloParent > 0 && (
                          <Typography>Solo Parent ID List</Typography>
                        )}

                        <Stack
                          direction='column'
                          sx={{
                            maxHeight: '280px',
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': {
                              width: '10px', // Width of the scrollbar
                            },
                          }}
                        >
                          {totalPaxSoloParent > 0 &&
                            totalPaxSoloParent <= 100 && (
                              <>
                                {errors.solo_parent_id?.id_list?.message && (
                                  <Typography
                                    sx={{
                                      fontFamily: 'Poppins',
                                      fontSize: 12,
                                      marginBottom: 1,
                                      marginTop: -1,
                                      marginLeft: 1,
                                      fontWeight: 400,
                                      lineHeight: '19.92px',
                                      letterSpacing: '0.4px',
                                      color: danger,
                                    }}
                                  >
                                    {errors.solo_parent_id?.id_list?.message}
                                  </Typography>
                                )}
                                <Stack gap={2}>
                                  {[...Array(totalPaxSoloParent)].map(
                                    (_, index) => (
                                      <Stack
                                        key={index}
                                        sx={{
                                          py: 1.5,
                                          px: 1,
                                          border: '2px solid',
                                          borderColor: 'secondary.500',
                                          borderRadius: '12px',
                                        }}
                                      >
                                        <InputFieldsController
                                          label={`Solo Parent ID No. ${index + 1} <span style="fontStyle: italic; color: #FF5630;" }}>${
                                            errors.solo_parent_id?.id_list?.[
                                              index
                                            ]?.id?.message ?? ''
                                          }</span>`}
                                          name={`solo_parent_id.id_list[${index}].id`}
                                          control={control}
                                          errors={errors}
                                          disabled={
                                            selectedDiscount !== 'rbSpecial'
                                          }
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position='start'>
                                                <BadgeOutlined />
                                              </InputAdornment>
                                            ),
                                            inputComponent: (
                                              props: CustomNumberFormatProps,
                                            ) => (
                                              <CustomNumberFormat
                                                {...props}
                                                isNegative={false}
                                                decimalScale={0}
                                                isAllowed={(values) =>
                                                  values.value.length <= 16
                                                }
                                                thousandSeparator={false}
                                              />
                                            ),
                                          }}
                                          trigger={trigger}
                                          validateTrigger={Boolean(
                                            errors.solo_parent_id?.id_list?.[
                                              index
                                            ]?.id?.message ?? false,
                                          )}
                                        />

                                        <InputFieldsController
                                          label={`Name ${index + 1} <span style="fontStyle: italic; color: #FF5630;" }}>${
                                            errors.solo_parent_id?.id_list?.[
                                              index
                                            ]?.name?.message ?? ''
                                          }</span>`}
                                          name={`solo_parent_id.id_list[${index}].name`}
                                          control={control}
                                          errors={errors}
                                          disabled={
                                            selectedDiscount !== 'rbSpecial'
                                          }
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position='start'>
                                                <PersonOutline />
                                              </InputAdornment>
                                            ),
                                          }}
                                          trigger={trigger}
                                          validateTrigger={Boolean(
                                            errors.solo_parent_id?.id_list?.[
                                              index
                                            ]?.name?.message ?? false,
                                          )}
                                        />

                                        <InputFieldsController
                                          label={`Name of Child ${index + 1} <span style="fontStyle: italic; color: #FF5630;" }}>${
                                            errors.solo_parent_id?.id_list?.[
                                              index
                                            ]?.childName?.message ?? ''
                                          }</span>`}
                                          name={`solo_parent_id.id_list[${index}].childName`}
                                          control={control}
                                          errors={errors}
                                          disabled={
                                            selectedDiscount !== 'rbSpecial'
                                          }
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position='start'>
                                                <SentimentSatisfiedAltOutlined />
                                              </InputAdornment>
                                            ),
                                          }}
                                          trigger={trigger}
                                          validateTrigger={Boolean(
                                            errors.solo_parent_id?.id_list?.[
                                              index
                                            ]?.childName?.message ?? false,
                                          )}
                                        />

                                        <DatePickerController
                                          control={control}
                                          label={`Birthdate of Child ${index + 1} <span style="fontStyle: italic; color: #FF5630;" }}>${
                                            errors.solo_parent_id?.id_list?.[
                                              index
                                            ]?.birthdate?.message ?? ''
                                          }</span>`}
                                          name={`solo_parent_id.id_list[${index}].birthdate`}
                                          errors={errors}
                                          disablePast={false}
                                          sx={{
                                            ...reversDatePickerStyle,
                                            mb: 2,
                                          }}
                                          disabled={
                                            selectedDiscount !== 'rbSpecial'
                                          }
                                          trigger={trigger}
                                          addOnChange={(selectedDate) => {
                                            if (!selectedDate) return;

                                            const today = moment();

                                            let age =
                                              today.year() -
                                              selectedDate.year();

                                            const isBirthdayPassed =
                                              today.month() >
                                                selectedDate.month() ||
                                              (today.month() ===
                                                selectedDate.month() &&
                                                today.date() >=
                                                  selectedDate.date());

                                            if (!isBirthdayPassed) {
                                              age -= 1;
                                            }

                                            setValue(
                                              `solo_parent_id.id_list[${index}].age` as any,
                                              age,
                                            );
                                          }}
                                          disableFuture
                                        />

                                        <InputFieldsController
                                          control={control}
                                          errors={errors}
                                          name={`solo_parent_id.id_list[${index}].age`}
                                          label={`Age of Child ${index + 1} <span style="fontStyle: italic; color: #FF5630;" }}>${
                                            errors.solo_parent_id?.id_list?.[
                                              index
                                            ]?.age?.message ?? ''
                                          }</span>`}
                                          wrapperStyle={{ marginBottom: 0 }}
                                          borderErrorOnly
                                          InputProps={{
                                            inputComponent: CustomNumberFormat,
                                            inputProps: {
                                              textAlign: 'left',
                                              allowNegative: false,
                                              decimalScale: 0,
                                              thousandSeparator: false,
                                            },
                                            startAdornment: (
                                              <InputAdornment position='start'>
                                                <ChildCareOutlined />
                                              </InputAdornment>
                                            ),
                                          }}
                                          handleOnChange={(value) => {
                                            const age = Number(value);

                                            if (!age || isNaN(age)) {
                                              setValue(
                                                `solo_parent_id.id_list[${index}].age` as any,
                                                0,
                                              );
                                              return;
                                            }

                                            const today = moment();

                                            const existingBirthdate = getValues(
                                              `solo_parent_id.id_list.${index}.birthdate`,
                                            );

                                            const base = existingBirthdate
                                              ? moment(existingBirthdate)
                                              : moment();

                                            const newBirthdate = base
                                              .clone()
                                              .year(today.year() - age);

                                            if (newBirthdate.isAfter(today)) {
                                              newBirthdate.subtract(1, 'year');
                                            }

                                            setValue(
                                              `solo_parent_id.id_list[${index}].birthdate` as any,
                                              moment(newBirthdate),
                                            );
                                          }}
                                        />
                                      </Stack>
                                    ),
                                  )}
                                </Stack>
                              </>
                            )}
                        </Stack>

                        {totalPaxSoloParent > 0 && (
                          <Divider
                            sx={{
                              pt: 1,
                              borderColor: 'primary.main',
                              borderBottomWidth: '2px',
                            }}
                          />
                        )}
                      </Stack>

                      {/*Athlete*/}
                      <Stack direction='column' gap='5px'>
                        {/* <Typography>Athlete</Typography> */}
                        <InputFieldsController
                          label={`${pathname === 'point-of-sale' ? 'NAAC' : 'Athlete'} Total Pax`}
                          name='athlete_pax'
                          control={control}
                          errors={errors}
                          wrapperStyle={{
                            minWidth: '150px',
                            width: '100%',
                            marginBottom: 0,
                          }}
                          inputProps={{
                            min: 0,
                            maxLength: totalPax,
                            style: { textAlign: 'right' },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position='start'>
                                <Numbers />
                              </InputAdornment>
                            ),
                          }}
                          type='number'
                          value={
                            selectedDiscount !== 'rbSpecial' ? 0 : undefined
                          }
                          disabled={selectedDiscount !== 'rbSpecial'}
                          onChange={(e) => {
                            const el = e.target;
                            let value = el.value
                              .trim()
                              .replace(/[^\d]|^0+/g, '');
                            const numericValue = Number(value);

                            if (!numericValue || numericValue <= 0) {
                              setValue('athlete_pax', 0);
                              el.value = '0';
                              return;
                            }

                            setValue('athlete_pax', numericValue);
                            el.value = String(numericValue);
                          }}
                        />
                        {totalPaxAthlete > 0 && (
                          <Typography>Athlete ID List</Typography>
                        )}
                        <Stack
                          direction='column'
                          sx={{
                            maxHeight: '120px',
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': {
                              width: '10px', // Width of the scrollbar
                            },
                          }}
                        >
                          {totalPaxAthlete > 0 && totalPaxAthlete <= 100 && (
                            <>
                              {errors.athlete_id?.id_list?.message && (
                                <Typography
                                  sx={{
                                    fontFamily: 'Poppins',
                                    fontSize: 12,
                                    marginBottom: 1,
                                    marginTop: -1,
                                    marginLeft: 1,
                                    fontWeight: 400,
                                    lineHeight: '19.92px',
                                    letterSpacing: '0.4px',
                                    color: danger,
                                  }}
                                >
                                  {errors.athlete_id?.id_list?.message}
                                </Typography>
                              )}
                              <Stack gap={2}>
                                {[...Array(totalPaxAthlete)].map((_, index) => (
                                  <Stack
                                    key={index}
                                    sx={{
                                      py: 1.5,
                                      px: 1,
                                      border: '2px solid',
                                      borderColor: 'secondary.500',
                                      borderRadius: '12px',
                                    }}
                                  >
                                    <InputFieldsController
                                      label={`Athlete ID No. ${index + 1} <span style="fontStyle: italic; color: #FF5630;" }}>${
                                        errors.athlete_id?.id_list?.[index]?.id
                                          ?.message ?? ''
                                      }</span>`}
                                      name={`athlete_id.id_list[${index}].id`}
                                      control={control}
                                      errors={errors}
                                      disabled={
                                        selectedDiscount !== 'rbSpecial'
                                      }
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position='start'>
                                            <BadgeOutlined />
                                          </InputAdornment>
                                        ),
                                        inputComponent: (
                                          props: CustomNumberFormatProps,
                                        ) => (
                                          <CustomNumberFormat
                                            {...props}
                                            isNegative={false}
                                            decimalScale={0}
                                            isAllowed={(values) =>
                                              values.value.length <= 16
                                            }
                                            thousandSeparator={false}
                                          />
                                        ),
                                      }}
                                      trigger={trigger}
                                      validateTrigger={Boolean(
                                        errors.athlete_id?.id_list?.[index]?.id
                                          ?.message ?? false,
                                      )}
                                    />
                                    <InputFieldsController
                                      label={`Name ${index + 1} <span style="fontStyle: italic; color: #FF5630;" }}>${
                                        errors.athlete_id?.id_list?.[index]
                                          ?.name?.message ?? ''
                                      }</span>`}
                                      name={`athlete_id.id_list[${index}].name`}
                                      control={control}
                                      errors={errors}
                                      disabled={
                                        selectedDiscount !== 'rbSpecial'
                                      }
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position='start'>
                                            <PersonOutline />
                                          </InputAdornment>
                                        ),
                                      }}
                                      trigger={trigger}
                                      validateTrigger={Boolean(
                                        errors.athlete_id?.id_list?.[index]
                                          ?.name?.message ?? false,
                                      )}
                                      wrapperStyle={{ marginBottom: 0 }}
                                    />
                                  </Stack>
                                ))}
                              </Stack>
                            </>
                          )}
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
              </RadioGroup>
            </FormControl>
          </Stack>

          {/* BUTTON */}
          <Stack
            direction='row'
            sx={{
              borderTop: '1px solid #EEE',
              p: 2,
              gap: 1,
              justifyContent: 'flex-end',
            }}
          >
            <Button variant='outlined' onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton
              variant='contained'
              type={!applyBtnTypeSubmit ? 'button' : 'submit'}
              onClick={
                !applyBtnTypeSubmit && !onConfirm
                  ? handleSubmit(onSubmit)
                  : onConfirm
                    ? handleManualSubmit
                    : undefined
              }
              loading={isLoadingManualSubmit}
            >
              Apply
            </LoadingButton>
          </Stack>
        </form>
        <DialogInformation />
      </Box>
    </FormProvider>
  );
};

export default FORoomPostingDiscountForm;
